import { Injectable } from "@angular/core";

import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { dateToDb, dbToBoolean, dbToString, floatToDb } from "@dffedb/util";
import { InstallationHttpClient, ParametreForAktuelleInstallation, UmbracoHttpClient } from "@e-forsyning/common/http";

import { FlyttemeddelelseIndstillingerDto } from "../model/flyttemeddelelse-indstillinger.dto";
import { FlyttemeddelelseIndstillinger } from "../model/flyttemeddelelse-indstillinger.model";
import { IFlyttemeddelelse } from "../model/flyttemeddelelse.model";

@Injectable({ providedIn: "root" })
export class FlyttemeddelelseRepository {
    constructor(private readonly http: InstallationHttpClient, private readonly umbracoHttp: UmbracoHttpClient) {}

    public hentIndstillinger(): Observable<FlyttemeddelelseIndstillinger> {
        return this.umbracoHttp
            .hentSideData<FlyttemeddelelseIndstillingerDto>("meld-flytning")
            .pipe(map((data) => this.mapDtoToIndstillinger(data)));
    }

    public opretFlyttemeddelelse(flyttemeddelelse: IFlyttemeddelelse): Observable<any> {
        return this.http.post("api/kvikflyt", this.mapFlyttemeddelelseToDto(flyttemeddelelse), ParametreForAktuelleInstallation.true);
    }

    private mapDtoToIndstillinger(dto: FlyttemeddelelseIndstillingerDto): FlyttemeddelelseIndstillinger {
        return {
            menuTekst: dbToString(dto.menuTekst),
            erAflaesningFravalgt: dbToBoolean(dto.fravaelgAflaesning),
            erUdflytterMailSynlig: dbToBoolean(dto.udf_email),
            topOverskrift: dbToString(dto.topOverskrift),
            topIndhold: dbToString(dto.topIndhold)
        };
    }

    private mapFlyttemeddelelseToDto(flyttemeddelelse: IFlyttemeddelelse): any {
        const taellevaerker = flyttemeddelelse.aflaesning.aflaesning.taellevaerker.map((a) => ({
            enhed: floatToDb(a.aflaesning.aktuel),
            enhedtype: a.enhedType
        }));
        return {
            aflaesopl: {
                aflaesoplysninger: [
                    {
                        ejendomnr: flyttemeddelelse.installation.ejendomNr,
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        i_nr: flyttemeddelelse.installation.installationNr,
                        aktivnr: flyttemeddelelse.installation.aktivNr,

                        anmeldtaf: flyttemeddelelse.anmelder.navn,
                        beholdejeradr: flyttemeddelelse.indflytter.beholdEjerAdresse,

                        dato: dateToDb(flyttemeddelelse.aflaesning.dato),
                        flytdato: dateToDb(flyttemeddelelse.udflytter.datoForUdflytning),

                        fravaelgAfl: flyttemeddelelse.aflaesning.erAflaesningFravalgt,
                        spgUdflEmail: flyttemeddelelse.udflytter.erUdflytterMailSynlig,

                        ufadresse: flyttemeddelelse.udflytter.adresse.vejnavn,
                        ufnavn: flyttemeddelelse.udflytter.navn,
                        ufpostnr: flyttemeddelelse.udflytter.adresse.postnr,
                        by: flyttemeddelelse.udflytter.adresse.bynavn,
                        email: flyttemeddelelse.udflytter.mail,

                        betalerBy: flyttemeddelelse.faktura.adresse.bynavn,
                        kontakttlf: flyttemeddelelse.anmelder.telefonnr,

                        ifadr2: flyttemeddelelse.indflytter.navnPaaPartner,
                        ifnavn: flyttemeddelelse.indflytter.navn,

                        opkradr1: flyttemeddelelse.faktura.navn,
                        opkradr2: flyttemeddelelse.faktura.adresse.vejnavn,
                        opkrpostnr: flyttemeddelelse.faktura.adresse.postnr,

                        regnr: flyttemeddelelse.udbetaling.registreringsnr,
                        kontonr: flyttemeddelelse.udbetaling.kontonr,

                        taellevaerker
                    }
                ]
            },
            flytteOpl: {
                // Installation
                aktivnr: flyttemeddelelse.installation.aktivNr,
                ejendomnr: flyttemeddelelse.installation.ejendomNr,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                i_nr: flyttemeddelelse.installation.installationNr,

                // Udflytter
                ufnavn: flyttemeddelelse.udflytter.navn,
                ufadresse: flyttemeddelelse.udflytter.adresse.vejnavn,
                ufpostnr: flyttemeddelelse.udflytter.adresse.postnr,
                by: flyttemeddelelse.udflytter.adresse.bynavn,
                email: flyttemeddelelse.udflytter.mail,
                flytdato: dateToDb(flyttemeddelelse.udflytter.datoForUdflytning),

                // Indflytter
                ifnavn: flyttemeddelelse.indflytter.navn,
                ifadr2: flyttemeddelelse.indflytter.navnPaaPartner,
                beholdejeradr: flyttemeddelelse.indflytter.beholdEjerAdresse,

                // Faktura
                opkradr1: flyttemeddelelse.faktura.navn,
                opkradr2: flyttemeddelelse.faktura.adresse.vejnavn,
                opkrpostnr: flyttemeddelelse.faktura.adresse.postnr,
                betalerBy: flyttemeddelelse.faktura.adresse.bynavn,

                // Udbetaling
                regnr: flyttemeddelelse.udbetaling.registreringsnr,
                kontonr: flyttemeddelelse.udbetaling.kontonr,

                // Anmelder
                anmeldtaf: flyttemeddelelse.anmelder.navn,
                kontakttlf: flyttemeddelelse.anmelder.telefonnr,

                fravaelgAfl: flyttemeddelelse.aflaesning.erAflaesningFravalgt,
                spgUdflEmail: flyttemeddelelse.udflytter.erUdflytterMailSynlig
            }
        };
    }
}
